// Gulf Monger — Delivery Zone Checker
//
// ── Config (update these when zone changes) ───────────────────────────────────
//   FULFILLMENT.address — human-readable label only
//   FULFILLMENT.lat/lng — pre-computed coordinates of that address
//   RADIUS_MILES        — must match Shopify Admin › Shipping › Local Delivery
//
// To change delivery origin: tell Claude "update fulfillment address to <new address>"
// To change radius: update RADIUS_MILES below, then push this file.
// ─────────────────────────────────────────────────────────────────────────────

const GM_ZONE = (() => {
  const FULFILLMENT = {
    address: '4515 S Shamrock Rd, Tampa, FL 33611',
    lat:      27.8759,
    lng:     -82.5175,
  };
  const RADIUS_MILES = 11.5;
  const GMAPS_KEY    = 'AIzaSyC3wVVfkPOvtng4Ig_LKVtP27vheRJsPc0';

  // ── Load Google Maps JS SDK (once) ───────────────────────────────────────────

  let _mapsReady = null;

  function loadMaps() {
    if (_mapsReady) return _mapsReady;
    _mapsReady = new Promise(resolve => {
      if (window.google?.maps?.places) { resolve(); return; }
      window.__gmZoneReady = resolve;
      const s = document.createElement('script');
      s.src   = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places&callback=__gmZoneReady`;
      s.async = true;
      document.head.appendChild(s);
    });
    return _mapsReady;
  }

  // ── Geo helpers ─────────────────────────────────────────────────────────────

  function haversine(lat1, lng1, lat2, lng2) {
    const R    = 3958.8;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a    = Math.sin(dLat / 2) ** 2 +
                 Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                 Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function geocodeAddress(address) {
    return new Promise(resolve => {
      new google.maps.Geocoder().geocode(
        { address, componentRestrictions: { country: 'US' } },
        (results, status) => {
          resolve(status === 'OK' && results.length ? results[0].geometry.location : null);
        }
      );
    });
  }

  // ── State ────────────────────────────────────────────────────────────────────

  let _blocked     = false;
  let _lastAddress = null;
  let _lastInZone  = null;

  // ── DOM helpers ──────────────────────────────────────────────────────────────

  function setCheckoutBlock(blocked) {
    _blocked = blocked;
    document.querySelectorAll('[data-checkout-link]').forEach(el => {
      if (blocked) el.setAttribute('data-zone-blocked', '');
      else         el.removeAttribute('data-zone-blocked');
    });
  }

  function renderResult(container, inZone, address) {
    if (!container) return;
    container.hidden = false;
    if (inZone === 'loading') {
      container.innerHTML = `<span class="dz-result dz-result--unknown">Checking&hellip;</span>`;
    } else if (inZone === true) {
      container.innerHTML = `
        <span class="dz-result dz-result--ok">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
          We deliver there &mdash; Tuesdays, 6&ndash;8 PM
        </span>`;
    } else if (inZone === false) {
      container.innerHTML = `
        <span class="dz-result dz-result--no">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          Outside our current delivery zone. <button type="button" class="dz-notify-btn" data-dz-notify>Get notified &rarr;</button>
        </span>`;
    } else {
      container.innerHTML = `
        <span class="dz-result dz-result--unknown">
          Address not found &mdash; try "123 Bay St, Tampa FL"
        </span>`;
    }
  }

  function getActiveAddress() {
    const widget = document.querySelector('[data-dz-widget]');
    return (widget?.querySelector('[data-dz-input]')?.value || '').trim();
  }

  // ── Places Autocomplete ──────────────────────────────────────────────────────

  function applyAutocomplete() {
    document.querySelectorAll('[data-dz-input]').forEach(input => {
      if (input._dzAC) return;
      const ac = new google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'us' },
        fields:                ['geometry', 'formatted_address'],
        types:                 ['geocode'],
      });
      input._dzAC = ac;
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place.geometry?.location) return;
        const lat    = place.geometry.location.lat();
        const lng    = place.geometry.location.lng();
        const dist   = haversine(FULFILLMENT.lat, FULFILLMENT.lng, lat, lng);
        const inZone = dist <= RADIUS_MILES;
        const widget = input.closest('[data-dz-widget]');
        const result = widget?.querySelector('[data-dz-result]');
        _lastAddress = place.formatted_address || input.value;
        _lastInZone  = inZone;
        renderResult(result, inZone, _lastAddress);
        setCheckoutBlock(!inZone);
      });
    });
  }

  // ── Check button handler (async — geocodes typed text) ───────────────────────

  async function handleCheck(widget) {
    const input   = widget?.querySelector('[data-dz-input]');
    const result  = widget?.querySelector('[data-dz-result]');
    const btn     = widget?.querySelector('[data-dz-check]');
    const address = (input?.value || '').trim();

    if (address.length < 3) {
      if (result) {
        result.hidden    = false;
        result.innerHTML = '<span class="dz-result dz-result--unknown">Enter your delivery address or zip code</span>';
      }
      return;
    }

    const origLabel = btn?.textContent;
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    renderResult(result, 'loading', address);

    try {
      await loadMaps();
      const loc = await geocodeAddress(address);

      if (!loc) {
        renderResult(result, null, address);
        return;
      }

      const dist   = haversine(FULFILLMENT.lat, FULFILLMENT.lng, loc.lat(), loc.lng());
      const inZone = dist <= RADIUS_MILES;

      _lastAddress = address;
      _lastInZone  = inZone;

      renderResult(result, inZone, address);
      setCheckoutBlock(!inZone);

    } catch (err) {
      console.error('[GM_ZONE] geocode failed', err);
      if (result) {
        result.hidden    = false;
        result.innerHTML = '<span class="dz-result dz-result--unknown">Something went wrong &mdash; try again</span>';
      }
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = origLabel; }
    }
  }

  // ── Post-cart-refresh re-apply ───────────────────────────────────────────────

  function reapplyAfterRefresh() {
    setCheckoutBlock(_blocked);
    if (window.google?.maps?.places) applyAutocomplete();
    if (!_lastAddress) return;
    document.querySelectorAll('[data-dz-widget]').forEach(widget => {
      const input  = widget.querySelector('[data-dz-input]');
      const result = widget.querySelector('[data-dz-result]');
      if (input) input.value = _lastAddress;
      if (result && _lastInZone !== null) renderResult(result, _lastInZone, _lastAddress);
    });
  }

  // ── Modal ─────────────────────────────────────────────────────────────────────

  function openModal(address) {
    const modal = document.getElementById('dz-modal');
    if (!modal) return;
    const addrField = modal.querySelector('[data-dz-address]');
    if (addrField && address) addrField.value = address;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('dz-modal-open');
    setTimeout(() => modal.querySelector('[data-dz-focus]')?.focus(), 80);
  }

  function closeModal() {
    const modal = document.getElementById('dz-modal');
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('dz-modal-open');
  }

  function isModalOpen() {
    return document.getElementById('dz-modal')?.getAttribute('aria-hidden') === 'false';
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    // Delegated (not per-element) so it still fires for inputs injected later —
    // e.g. the cart drawer re-renders its address field from scratch on every
    // cart refresh, which would otherwise lose a directly-attached listener.
    document.addEventListener('focusin', e => {
      if (e.target.matches?.('[data-dz-input]') && !e.target._dzAC) {
        loadMaps().then(applyAutocomplete);
      }
    });

    document.addEventListener('click', e => {
      if (e.target.closest('[data-dz-check]')) {
        e.preventDefault();
        handleCheck(e.target.closest('[data-dz-widget]'));
        return;
      }
      if (e.target.closest('[data-dz-notify]')) {
        e.preventDefault();
        openModal(getActiveAddress());
        return;
      }
      if (e.target.closest('[data-dz-close]') ||
          (e.target.id === 'dz-modal' && !e.target.closest('.dz-modal__box'))) {
        closeModal();
        return;
      }
      const coLink = e.target.closest('[data-checkout-link]');
      if (coLink?.hasAttribute('data-zone-blocked')) {
        e.preventDefault();
        openModal(getActiveAddress());
        return;
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && isModalOpen()) {
        e.stopImmediatePropagation();
        closeModal();
      }
      if (e.key === 'Enter' && e.target.matches('[data-dz-input]')) {
        e.preventDefault();
        handleCheck(e.target.closest('[data-dz-widget]'));
      }
    });

    document.addEventListener('cart:refreshed', reapplyAfterRefresh);

    document.addEventListener('submit', e => {
      const form = e.target.closest('#dz-contact-form');
      if (!form) return;
      e.preventDefault();

      const fd      = new FormData(form);
      const address = (form.querySelector('[data-dz-address]')?.value || '').trim();

      fd.set('contact[body]',
        `Delivery zone expansion request\n\nDelivery address: ${address}`);

      const inner = form.closest('.dz-modal__inner');

      fetch('/contact', { method: 'POST', body: fd })
        .finally(() => {
          if (!inner) return;
          inner.innerHTML = `
            <div class="dz-thanks">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#15803d" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="20 6 9 17 4 12"/></svg>
              <h2 class="dz-thanks__heading">You're on the list.</h2>
              <p class="dz-thanks__body">We'll reach out as soon as we expand to your area. Thanks for your support.</p>
              <button class="btn btn--primary dz-thanks__btn" data-dz-close>Done</button>
            </div>`;
        });
    });
  }

  return { init };
})();

document.addEventListener('DOMContentLoaded', () => GM_ZONE.init());
